import { Stack } from "@mui/material";
import React, { useCallback } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  TooltipProps,
  XAxis,
  YAxis,
} from "recharts";

import { labelInternalToUI, labelMappings } from "config/LabelMapping";
import { Typography } from "new-components/typography/Typography";

export interface HistogramEntry {
  "ML Confident": number;
  Escalated: number;
  val?: number;
}

export interface HistogramProps {
  histogramData: HistogramEntry[];
  confidenceThreshold: number;
  isPreview?: boolean;
}

export function Histogram(props: HistogramProps) {
  // const theme = useTheme();
  // const isMD = useMediaQuery(theme.breakpoints.up("sm"));
  const { histogramData, confidenceThreshold, isPreview } = props;
  const CustomTooltip = useCallback((props: TooltipProps<number, string>): JSX.Element | null => {
    const { active, payload } = props;
    if (active && payload && payload.length) {
      const entry: HistogramEntry = payload[0].payload;
      let val = entry.val || 0;
      let textColor = "text-hl-magenta";
      if (val < 50) {
        val = 50 + (49 - val);
        textColor = "text-hl-purple";
      }
      return (
        <Stack className="shadow-md rounded p-2 bg-neutral-100">
          <Typography>{(val * 0.01)?.toFixed(2)}</Typography>
          <Typography variant="small" className={`${textColor}`}>ML Confident: {payload[0].payload["ML Confident"]}</Typography>
          <Typography variant="small" className={`${textColor}`}>Escalated: {payload[0].payload.Escalated}</Typography>
        </Stack>
      );
    }

    return null;
  }, []);

  const CustomXAxisTick = useCallback((props: { x: number; y: number; payload: { value: number } }) => {
    const { x, y, payload } = props;
    const intVal = payload.value;
    let tickVal;
    let fill;
    if (intVal < 50) {
      // midpoint
      if (intVal === 49.5) {
        tickVal = 0.5;
        fill = "#888";
      } else {
        // YES percentages
        tickVal = (99 - intVal) * 0.01;
        fill = "#5E17E6";
      }
    } else {
      // NO percentages
      tickVal = intVal * 0.01;
      fill = "#CA00EB";
    }

    let tick = tickVal?.toFixed(2).replaceAll("0", "");

    let dx = 0;
    if (intVal === 0) {
      tick = `1.0 ${labelInternalToUI(labelMappings, "PASS")}`;
      dx = 20;
    }
    if (intVal === 99) {
      tick = `1.0 ${labelInternalToUI(labelMappings, "FAIL")}`;
    }

    return (
      <g transform={`translate(${x},${y})`}>
        <text fontSize={12} x={5} y={10} dx={dx} textAnchor="end" fill={fill}>
          {tick}
        </text>
      </g>
    );
  }, []);

  const tickFormatterFunc = (value: string) => (isPreview ? "" : value);
  const leftPosition = isPreview ? -60 : -30;

  return (
    <ResponsiveContainer
      // debounce={100}
      data-testid="ml-confident-bar"
      width="100%"
      height="100%"
    >
      <BarChart
        data={histogramData}
        margin={{ top: 0.5, right: 0, bottom: 0.5, left: leftPosition }}
        style={{ fontSize: "0.7rem" }}
        role="region"
      >
        <XAxis dataKey="val" type="number" ticks={[0, 24, 49.5, 75, 99]} tick={CustomXAxisTick} hide={!!isPreview} />
        <ReferenceLine x={49.5} stroke="#ccc" strokeDasharray="3 3" />
        <ReferenceLine x={Math.floor((1 - confidenceThreshold) * 100) - 1} stroke="#5E17E6" strokeDasharray="3 3" />
        <ReferenceLine x={Math.floor(confidenceThreshold * 100)} stroke="#CA00EB" strokeDasharray="3 3" />
        <YAxis allowDecimals={false} axisLine={!isPreview} tickLine={!isPreview} tickFormatter={tickFormatterFunc} />
        <Tooltip content={<CustomTooltip />} />
        <Bar dataKey="ML Confident" barSize={10} stackId="a" fill="#5E17E6" fillOpacity={0.4}>
          {histogramData?.map((entry, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <Cell key={`${entry.val}-${index}`} fill={index < 50 ? "#5E17E6" : "#CA00EB"} />
          ))}
        </Bar>
        <Bar dataKey="Escalated" barSize={10} stackId="a">
          {histogramData?.map((entry, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <Cell key={`${entry.val}-${index}`} fill={index < 50 ? "#5E17E6" : "#CA00EB"} />
          ))}
        </Bar>
        <CartesianGrid vertical={false} stroke="#ccc" strokeDasharray="3 3" />
      </BarChart>
    </ResponsiveContainer>
  );
}

export default Histogram;
